// built-from: /done-repo-projection v1 impl:done-repo-projection role:implementation
// built-from: /knowledge-files-view v1 impl:knowledge-files-view role:implementation
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEngagement } from './useEngagement';

// 090-engagement-knowledge-tree: Done files the page in the engagement's
// knowledge base, not in a connected repository. There is no deferred state any
// more: the write either lands or it says so.
export type DoneProjectionStatus = 'idle' | 'dirty' | 'saving' | 'filed' | 'failed';

export const DONE_PROJECTION_STATUS_TEXT: Record<DoneProjectionStatus, string> = {
  idle: 'Changes not filed yet',
  dirty: 'Changes not filed yet',
  saving: 'Saving...',
  filed: 'Filed in the knowledge base',
  failed: 'Could not file this record. Your work is saved.',
};

export type DoneProjectionResult =
  | { ok: true; projection: { status: string; path?: string } }
  | { ok: false; reason: 'unavailable' };

export interface UseDoneProjectionReturn {
  projectDone: (screenId: string) => Promise<DoneProjectionResult>;
}

export interface UseDoneProjectionActionArgs {
  screenId: string;
  saveCurrent: () => Promise<void>;
}

export interface UseDoneProjectionActionReturn {
  doneStatus: DoneProjectionStatus;
  doneBusy: boolean;
  repoStatusText: string | null;
  markDoneDirty: () => void;
  onDone: () => Promise<void>;
}

const UNAVAILABLE: DoneProjectionResult = { ok: false, reason: 'unavailable' };

function readProjection(data: unknown): { status: string; path?: string } | null {
  if (!data || typeof data !== 'object') return null;
  const projection = (data as { projection?: unknown }).projection;
  if (!projection || typeof projection !== 'object') return null;
  const record = projection as { status?: unknown; path?: unknown };
  if (typeof record.status !== 'string') return null;
  return {
    status: record.status,
    path: typeof record.path === 'string' ? record.path : undefined,
  };
}

export function useDoneProjection(): UseDoneProjectionReturn {
  const { currentEngagementId } = useEngagement();

  const projectDone = useCallback(
    async (screenId: string): Promise<DoneProjectionResult> => {
      if (!currentEngagementId) return UNAVAILABLE;
      try {
        const res = await fetch(
          `/api/done/${encodeURIComponent(currentEngagementId)}/${encodeURIComponent(screenId)}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
          },
        );
        if (!res.ok) return UNAVAILABLE;
        const data = await res.json();
        const projection = readProjection(data);
        if (!projection) return UNAVAILABLE;
        return { ok: true, projection };
      } catch {
        return UNAVAILABLE;
      }
    },
    [currentEngagementId],
  );

  return { projectDone };
}

export function useDoneProjectionAction({
  screenId,
  saveCurrent,
}: UseDoneProjectionActionArgs): UseDoneProjectionActionReturn {
  const { currentEngagementId } = useEngagement();
  const { projectDone } = useDoneProjection();
  const navigate = useNavigate();
  const [doneStatus, setDoneStatus] = useState<DoneProjectionStatus>('idle');
  const [doneBusy, setDoneBusy] = useState(false);

  const markDoneDirty = useCallback(() => {
    setDoneStatus((status) => (status === 'saving' ? status : 'dirty'));
  }, []);

  const onDone = useCallback(async () => {
    if (doneBusy) return;

    setDoneBusy(true);
    setDoneStatus('saving');
    try {
      await saveCurrent();
      if (!currentEngagementId) {
        navigate('/');
        return;
      }
      const res = await projectDone(screenId);
      if (res.ok && res.projection.status === 'written') {
        setDoneStatus('filed');
        navigate('/');
        return;
      }
      setDoneStatus('failed');
    } catch {
      setDoneStatus('failed');
    } finally {
      setDoneBusy(false);
    }
  }, [currentEngagementId, doneBusy, navigate, projectDone, saveCurrent, screenId]);

  return {
    doneStatus,
    doneBusy,
    repoStatusText: currentEngagementId ? DONE_PROJECTION_STATUS_TEXT[doneStatus] : null,
    markDoneDirty,
    onDone,
  };
}
